import type { QueryClient } from '@tanstack/react-query';
import type { Message, Reaction, Thread } from '@/shared/api/schemas';
import { keys } from './keys';
import { patchMessage, type LocalPage } from './localMessages';

/** Реакция «туда-обратно»: мой голос в списке меняется сразу, без ожидания сети. */
export function toggleMine(reactions: readonly Reaction[], emoji: string, me: string): Reaction[] {
  const has = reactions.some((r) => r.emoji === emoji && r.userIds.includes(me));
  if (has) return reactions.map((r) => (r.emoji === emoji ? { ...r, userIds: r.userIds.filter((u) => u !== me) } : r)).filter((r) => r.userIds.length > 0);
  if (reactions.some((r) => r.emoji === emoji)) return reactions.map((r) => (r.emoji === emoji ? { ...r, userIds: [...r.userIds, me] } : r));
  return [...reactions, { emoji, userIds: [me] }];
}

interface Pending {
  count: number;
  before: readonly Reaction[];
}
/** Сообщения, у которых реакция ещё летит на сервер: пока их запросы в пути, «эхо» сервера не перезаписывает экран. */
const pending = new Map<string, Pending>();

export function hasPendingReaction(messageId: string): boolean {
  return pending.has(messageId);
}

export interface ReactionTarget {
  readonly wid: string;
  readonly messageId: string;
  readonly conversationId: string;
  readonly threadRootId: string | null;
}

function write(client: QueryClient, t: ReactionTarget, patch: (r: readonly Reaction[]) => readonly Reaction[]): void {
  client.setQueryData<LocalPage>(keys.messages(t.wid, t.conversationId), (old) => (old === undefined ? old : patchMessage(old, t.messageId, (m) => ({ ...m, reactions: [...patch(m.reactions)] }))));
  client.setQueryData<Thread>(keys.thread(t.wid, t.threadRootId ?? t.messageId), (old) => {
    if (old === undefined) return old;
    if (old.root.id === t.messageId) return { ...old, root: { ...old.root, reactions: [...patch(old.root.reactions)] } };
    return { ...old, replies: old.replies.map((r) => (r.id === t.messageId ? { ...r, reactions: [...patch(r.reactions)] } : r)) };
  });
  client.setQueryData<Message>(keys.message(t.wid, t.messageId), (old) => (old === undefined ? old : { ...old, reactions: [...patch(old.reactions)] }));
}

function current(client: QueryClient, t: ReactionTarget, fallback: readonly Reaction[]): readonly Reaction[] {
  return (
    client.getQueryData<LocalPage>(keys.messages(t.wid, t.conversationId))?.messages.find((m) => m.id === t.messageId)?.reactions ??
    client.getQueryData<Thread>(keys.thread(t.wid, t.threadRootId ?? t.messageId))?.replies.find((m) => m.id === t.messageId)?.reactions ??
    client.getQueryData<Thread>(keys.thread(t.wid, t.messageId))?.root.reactions ??
    fallback
  );
}

/** Показать реакцию в этот же кадр. Возвращает то, что было до неё. */
export function applyOptimistic(client: QueryClient, t: ReactionTarget, emoji: string, me: string, fallback: readonly Reaction[]): void {
  const entry = pending.get(t.messageId) ?? { count: 0, before: current(client, t, fallback) };
  entry.count += 1;
  pending.set(t.messageId, entry);
  write(client, t, (r) => toggleMine(r, emoji, me));
}

/** Ответ сервера: когда в пути больше нет запросов — ставим его (последний) список; ошибка — тихо откатываем. */
export function settleOptimistic(client: QueryClient, t: ReactionTarget, outcome: { readonly ok: true; readonly reactions: readonly Reaction[] } | { readonly ok: false }): void {
  const entry = pending.get(t.messageId);
  if (entry === undefined) return;
  entry.count -= 1;
  if (outcome.ok) entry.before = outcome.reactions;
  if (entry.count > 0) return;
  pending.delete(t.messageId);
  write(client, t, () => entry.before);
}
